/**
 * Shared geometry for the global banner cards — the announcement and the Fellows
 * promo, which render together in the secure layout when both are eligible.
 *
 * App-level messages are objects sitting on the page rather than chrome fused to
 * the nav. That is what earns the card treatment: rounded, bordered, and floated
 * off a ground, so two of them stacked read as two surfaces rather than one band
 * with a line through it.
 *
 * The floor keeps a single-line announcement from sitting noticeably shorter
 * than the promo beside it, which made the pair read as a card and an offcut
 * rather than two of the same thing. It is a floor, not a fixed height: at narrow
 * widths both wrap to a column and grow past it by different amounts. It tracks
 * the promo, which is the taller of the two.
 *
 * Vertical centering rides along here because it belongs on the element that
 * owns the height. The container inside is only as tall as its contents, so
 * centering there has nothing to center against and the shorter card sits high.
 *
 * This applies to the promo and announcement tiers. System and status banners
 * stay full-bleed: an outage or an impersonation notice is a state the whole app
 * is in, not a message sitting on the page, and edge-to-edge is what says so.
 */
export const GLOBAL_BANNER_CARD =
  'relative z-10 mx-4 min-h-16 justify-center rounded-xl border border-primary-200 shadow-soft-3';

/**
 * The strip the cards float on, in the same brand fill as the nav above it. The
 * slot reads as part of the header rather than as a gap punched between the nav
 * and the screen, and a pale card needs a darker ground to read as a card at
 * all: on the page background the fill is nearly the same value as the page, so
 * the corners and shadow do no work.
 */
export const GLOBAL_BANNER_SURFACE = 'w-full bg-primary-500 py-3';

/**
 * The gap between stacked cards. Enough brand ground shows through for the two
 * to read as separate surfaces, which is what the tier order is carried by here
 * — there is no divider between banners, so the gap is doing that work.
 */
export const GLOBAL_BANNER_STACK = 'gap-3';

/**
 * The card's contents are measured from the card, not from the page grid beneath
 * it. A floating card is its own container: aligning its copy to the screen's
 * column while its dismiss sat on its own corner left the two coming from
 * different boxes, which on a wide window stranded the dismiss far from the call
 * to action with a band of empty fill between them.
 */
export const GLOBAL_BANNER_CONTENT = 'w-full px-4 lg:px-6';

/**
 * Where an Account screen banner's text sits inside its band: the same side padding as the
 * rows (`px-5`), and on md and up held to the 672px settings column. The band is full-bleed on
 * web, so there the text's side padding is dropped to line up with the rows. Native has no
 * full-bleed band at that width: the banner sits inside the column itself, so it keeps its
 * padding. Both the away and update banners take their content area from this one string, so
 * their padding and width cannot drift apart.
 */
export const ACCOUNT_BANNER_CONTENT = 'px-5 md:max-w-2xl web:md:px-0';
